/**
 * AccessRequestsAdmin — Maker + Holding Admin approval queue.
 *
 * Lists access requests submitted via the Login page (any unknown
 * email entered there lands here). Admin can:
 *   1. Approve → opens a dialog to pick role, tenant (Maker only),
 *      and an optional welcome message. On submit, a real user is
 *      provisioned and a magic-link emailed automatically.
 *   2. Deny    → adds an optional reason; the request stays on file
 *      for audit purposes.
 *
 * Pending count is also surfaced as a sidebar nav badge via
 * usePendingAccessCount().
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  ShieldCheck, CheckCircle2, XCircle, Mail, Loader2, Filter, Clock,
  RefreshCw, User as UserIcon, Building2, Info, X, Send,
} from 'lucide-react';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on fetch effects. */

const STATUS_TABS = [
  { id: 'pending',  label: 'Pending',  icon: Clock,        accent: 'amber' },
  { id: 'approved', label: 'Approved', icon: CheckCircle2, accent: 'emerald' },
  { id: 'denied',   label: 'Denied',   icon: XCircle,      accent: 'rose' },
  { id: 'all',      label: 'All',      icon: Filter,       accent: 'slate' },
];

const ROLE_OPTIONS = [
  { id: 'viewer',         label: 'Viewer (read-only)' },
  { id: 'guest',          label: 'Guest' },
  { id: 'investor',       label: 'Investor' },
  { id: 'founder',        label: 'Founder' },
  { id: 'startup_member', label: 'Startup member' },
  { id: 'legal',          label: 'Legal' },
  { id: 'cfo',            label: 'CFO' },
  { id: 'ceo',            label: 'CEO' },
  { id: 'chairman',       label: 'Chairman' },
  { id: 'holding_admin',  label: 'Holding Admin' },
  { id: 'system_admin',   label: 'System Admin (Maker)', makerOnly: true },
];

function StatusBadge({ status }) {
  const cls = {
    pending:  'bg-amber-500/15   text-amber-700  dark:text-amber-300  border-amber-500/40',
    approved: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    denied:   'bg-rose-500/15    text-rose-700   dark:text-rose-300   border-rose-500/40',
  }[status] || 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30';
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded border ${cls}`}>
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Approve dialog — Maker can override tenant + role; holding_admin
//  is pinned to their own tenant.
// ─────────────────────────────────────────────────────────────────────
function ApproveDialog({ row, isMaker, onClose, onApproved }) {
  const [role, setRole] = useState('viewer');
  const [tenantId, setTenantId] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [name, setName] = useState('');
  const [sendLink, setSendLink] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (!isMaker) return;
    api.get('/admin/tenants/list').then(({ data }) => {
      setTenants(data?.rows || []);
    }).catch(() => {});
  }, [isMaker]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        role,
        name: name.trim() || undefined,
        welcome_message: welcomeMessage.trim() || undefined,
        send_link: sendLink,
      };
      if (isMaker && tenantId.trim()) body.tenant_id = tenantId.trim();
      const { data } = await api.post(
        `/admin/access-requests/${row.request_id}/approve`,
        body,
      );
      toast.success(`Approved — user ${data.user_id} provisioned`);
      onApproved(data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Approval failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const roleOptions = ROLE_OPTIONS.filter((r) => !r.makerOnly || isMaker);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="approve-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white dark:bg-zinc-900 border border-emerald-500/40 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-white/10 flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Approve access request</h2>
          <button type="button" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700 dark:hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-md bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 px-3 py-2.5 text-xs">
            <div className="font-mono text-slate-900 dark:text-white">{row.email}</div>
            {row.message && (
              <div className="text-slate-600 dark:text-white/65 mt-1.5 italic">
                &ldquo;{row.message}&rdquo;
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Full name <span className="opacity-60 normal-case font-normal">(optional — defaults from email)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={row.email.split('@')[0].replace('.', ' ')}
              data-testid="approve-name-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Role *
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="approve-role-select"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            >
              {roleOptions.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </div>

          {isMaker && (
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
                Tenant <span className="opacity-60 normal-case font-normal">(Maker only · defaults to primary)</span>
              </label>
              <select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                data-testid="approve-tenant-select"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              >
                <option value="">primary (default)</option>
                {tenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_id} · {t.brand_name || t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Welcome note <span className="opacity-60 normal-case font-normal">(optional — added to the sign-in email)</span>
            </label>
            <textarea
              rows={3}
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              placeholder="Welcome aboard — looking forward to working with you."
              data-testid="approve-welcome-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendLink}
              onChange={(e) => setSendLink(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-slate-700 dark:text-white/85">
              Auto-email a sign-in link now
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-zinc-950/40">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 dark:text-white/80 hover:bg-slate-100 dark:hover:bg-white/[0.06]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="approve-confirm-button"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Approve & send link
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Deny dialog
// ─────────────────────────────────────────────────────────────────────
function DenyDialog({ row, onClose, onDenied }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/access-requests/${row.request_id}/deny`,
        { reason: reason.trim() || undefined },
      );
      toast.success('Request denied');
      onDenied(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Deny failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="deny-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white dark:bg-zinc-900 border border-rose-500/40 rounded-xl w-full max-w-md shadow-2xl"
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-white/10 flex items-center gap-2">
          <XCircle size={18} className="text-rose-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Deny access request</h2>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-slate-700 dark:text-white/85">
            Deny access for <strong>{row.email}</strong>?
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Reason <span className="opacity-60 normal-case font-normal">(optional, for audit log only)</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Not a current customer / mistake / spam…"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-zinc-950/40">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 dark:text-white/80 hover:bg-slate-100 dark:hover:bg-white/[0.06]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="deny-confirm-button"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
            Deny request
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────────────────
export default function AccessRequestsAdmin() {
  const { user } = useAuth();
  const isMaker = (user?.real_role || user?.role) === 'system_admin';

  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveRow, setApproveRow] = useState(null);
  const [denyRow, setDenyRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === 'all' ? {} : { status: tab };
      const { data } = await api.get('/admin/access-requests', { params });
      setRows(data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (s) => {
    if (!s) return '—';
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="access-requests-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
            <ShieldCheck size={22} />
          </span>
          <div>
            <h1 className="text-3xl font-serif font-bold text-slate-900 dark:text-white">
              Access Requests
            </h1>
            <p className="text-sm text-slate-600 dark:text-white/65 mt-0.5">
              Approve or deny sign-in requests from new users. Existing users
              get an instant magic link without coming through here.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="refresh-button"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 text-sm font-semibold text-slate-700 dark:text-white/80 hover:bg-slate-100 dark:hover:bg-white/[0.06]"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="status-tabs">
        {STATUS_TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-bold border transition-colors ${
                active
                  ? 'bg-emerald-600 text-white border-emerald-700'
                  : 'bg-white dark:bg-zinc-900 text-slate-700 dark:text-white/75 border-slate-300 dark:border-white/15 hover:border-emerald-400'
              }`}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 dark:text-white/55" data-testid="loading">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="empty-state"
          className="text-center py-12 border border-dashed border-slate-300 dark:border-white/10 rounded-lg text-slate-500 dark:text-white/55"
        >
          <Mail size={28} className="mx-auto mb-2 opacity-50" />
          <div className="text-sm font-medium">No {tab === 'all' ? '' : tab} access requests.</div>
          <div className="text-xs mt-1">When a new user enters their email on /login, it lands here.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="requests-list">
          {rows.map((r) => (
            <div
              key={r.request_id}
              data-testid={`request-row-${r.request_id}`}
              className="bg-white dark:bg-zinc-900/60 border border-slate-200 dark:border-white/10 rounded-xl p-4 flex items-start gap-4 flex-wrap hover:border-emerald-400/60"
            >
              <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border border-emerald-500/20 shrink-0">
                <UserIcon size={18} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm text-slate-900 dark:text-white truncate" title={r.email}>{r.email}</span>
                  <StatusBadge status={r.status} />
                  {r.resolved_role && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30 font-bold">
                      {r.resolved_role}
                    </span>
                  )}
                  {r.resolved_tenant_id && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-200/60 dark:bg-white/[0.08] text-slate-600 dark:text-white/65 border border-slate-300 dark:border-white/15 font-bold inline-flex items-center gap-1">
                      <Building2 size={10} /> {r.resolved_tenant_id}
                    </span>
                  )}
                </div>
                {r.message && (
                  <div className="text-xs text-slate-600 dark:text-white/65 italic mt-1.5">
                    &ldquo;{r.message}&rdquo;
                  </div>
                )}
                <div className="text-[11px] text-slate-500 dark:text-white/50 mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>Requested: {fmtDate(r.requested_at)}</span>
                  {r.requested_ip && <span>IP: <span className="font-mono">{r.requested_ip}</span></span>}
                  {r.resolved_at && <span>Resolved: {fmtDate(r.resolved_at)}</span>}
                </div>
              </div>
              {r.status === 'pending' && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setApproveRow(r)}
                    data-testid={`approve-${r.request_id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider"
                  >
                    <CheckCircle2 size={13} /> Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => setDenyRow(r)}
                    data-testid={`deny-${r.request_id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wider"
                  >
                    <XCircle size={13} /> Deny
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {approveRow && (
        <ApproveDialog
          row={approveRow}
          isMaker={isMaker}
          onClose={() => setApproveRow(null)}
          onApproved={load}
        />
      )}
      {denyRow && (
        <DenyDialog
          row={denyRow}
          onClose={() => setDenyRow(null)}
          onDenied={load}
        />
      )}
    </div>
  );
}
